export const translations = {
  en: {
    brand: "AACBT",
    nav: { about: "About", services: "Services", team: "Team", resources: "Resources", contact: "Contact", inbox: "Inbox" },
    auth: { login: "Log in", signup: "Sign up", signout: "Sign out", admin: "Admin", myAccount: "My account" },
    hero: {
      title: "Evidence-informed programs for a calmer, clearer mind.",
      body: "Practical tools and structured learning paths—delivered online or in person. Learn skills, build resilience, and move forward.",
      cta: "Get Started", explore: "Explore resources",
      features: [
        { title: "Stress & Focus", text: "Tools to reduce worry and improve focus." },
        { title: "Skills First", text: "Safe, paced, and collaborative approaches." },
        { title: "For Teams & Individuals", text: "Age- and context-appropriate programs." }
      ]
    },
    highlights: ["✔️ Research-based content","✔️ Clear, structured modules","✔️ Privacy-first","✔️ Online & in-person options"],
    about: {
      eyebrow: "About",
      title: "Practical methods, simplified",
      copy: "We focus on actionable skills you can use right away. Structured, transparent plans so you always know what's next.",
      cards: [
        { title: "Structured & goal-oriented", text: "Clear milestones and progress tracking." },
        { title: "Short or long-form", text: "From brief skill sprints to deeper programs." },
        { title: "Transparent & ethical", text: "Up-front expectations and privacy by design." }
      ]
    },
    services: {
      eyebrow: "Services", title: "What we offer", copy: "Designed for clarity, flexibility, and real-world impact.", allTitle: "All services", allCopy: "Deep-dive into what we offer.",
      items: [
        { title: "Stress & Anxiety Tools", text: "Daily practices for calm and clarity." },
        { title: "Habits & Focus", text: "Evidence-informed routines for consistency." },
        { title: "Programs for Teams", text: "Lightweight workshops and follow-ups." },
        { title: "Resilience Skills", text: "Gradual exposure to build confidence." },
        { title: "Learning Library", text: "Guides, checklists, and mini-courses." },
        { title: "Online Sessions", text: "Flexible, privacy-first scheduling." }
      ]
    },
    team: { eyebrow: "Team", title: "Meet our group", copy: "A small, dedicated team focused on clear, actionable methods.", loading: "Loading team roster...", signInToView: "Sign in to view the team roster.", none: "No team members available yet.", member: "Member", approved: "Approved", pending: "Pending approval", verifiedBy: "Verified by" },
    resources: { eyebrow: "Resources", title: "Library", copy: "Books, guides, and videos curated for quick wins.", books: "Books", resources: "Resources", view: "View", by: "By", noneBooks: "Books will appear here as the library grows.", noneRes: "Articles and videos will appear here once added.", items: "item", itemsPlural: "items", noLink: "No link provided" },
    contact: {
      eyebrow: "Contact", title: "Get in touch", copy: "Tell us a bit about your needs. We’ll get back to you by email.",
      sendAMessage: "Send a message", howWeWork: "How we work",
      bullets: ["We’re a small group; replies usually within 1 business day.","No phone calls needed — we coordinate over email.","Share only what you’re comfortable sharing."],
      name: "Full name", email: "Email", subject: "Subject", message: "Your message...", send: "Send",
      ok: "Thanks! We received your message.", err: "Unable to send message right now.", sending: "Sending..."
    },
    notFound: { title: "Page not found", copy: "The page you’re looking for doesn’t exist.", goHome: "Go home" },
    footer: { privacy: "Privacy", accessibility: "Accessibility", terms: "Terms", rights: "All rights reserved." },
    privacyPage: {
      title: "Privacy policy",
      updated: "Last updated: OCT 2025",
      intro: [
        "We respect your privacy and limit the personal data we collect.",
        "This policy outlines what information we gather, how we use it, and how you can exercise your rights."
      ],
      sections: [
        {
          title: "Information we collect",
          body: [
            "We collect the minimum amount of personal data needed to deliver our services and support our members."
          ],
          items: [
            "Contact details you share when you send us a message.",
            "Account details such as your name, email address, and role when you sign up.",
            "Service usage metrics that are aggregated and anonymised for performance monitoring."
          ]
        },
        {
          title: "How we use information",
          body: [
            "We only process personal data for clear, documented purposes that relate to the services you request."
          ],
          items: [
            "Responding to enquiries and providing the services you ask us to deliver.",
            "Maintaining secure authentication and authorisation for members and admins.",
            "Improving program materials by reviewing anonymised usage trends."
          ]
        },
        {
          title: "How we store and protect data",
          body: [
            "Information is stored in encrypted, access-controlled systems. Access is restricted to trained team members who need the data to perform their role."
          ],
          items: [
            "We review access permissions regularly and revoke accounts that are no longer required.",
            "Backups are encrypted and retained for a limited period to support disaster recovery.",
            "We never sell personal information or share it with advertisers."
          ]
        },
        {
          title: "Your rights and choices",
          body: [
            "You decide how we use your information."
          ],
          items: [
            "Request a copy of the personal data we hold about you.",
            "Ask us to correct inaccurate information or delete your account.",
            "Withdraw consent for optional communications at any time."
          ],
          note: "We respond to verified privacy requests within two business days."
        }
      ],
      contact: {
        title: "Questions about privacy?",
        body: [
          "Email our privacy lead and we will get back to you quickly."
        ],
        email: "privacy@aacbt.de"
      }
    },
    termsPage: {
      title: "Terms of service",
      updated: "Effective from OCT 2025",
      intro: [
        "By using AACBT services you agree to these terms.",
        "If you have a separate written agreement with us, that agreement prevails where it conflicts with these terms."
      ],
      sections: [
        {
          title: "Who can use the service",
          body: [
            "AACBT programs are designed for adults who can form a binding agreement."
          ],
          items: [
            "You must be at least 18 years old or have consent from a parent or guardian.",
            "You agree to provide accurate information when creating or updating an account.",
            "You are responsible for safeguarding your login credentials."
          ]
        },
        {
          title: "Using the service responsibly",
          body: [
            "We provide educational content and practical tools. AACBT is not a substitute for personalised medical or mental-health care."
          ],
          items: [
            "Do not use the services in ways that violate applicable laws or regulations.",
            "Avoid sharing private information about others without their consent.",
            "Contact a licensed professional or emergency services if you require urgent support."
          ]
        },
        {
          title: "Content and intellectual property",
          body: [
            "All learning materials, branding, and platform code remain the property of AACBT or our licensors."
          ],
          items: [
            "You receive a limited, revocable licence to use the resources for your own development or the needs of your team.",
            "Do not copy, resell, or redistribute materials without written permission.",
            "Feedback you choose to share may be used to improve the service without attribution."
          ]
        },
        {
          title: "Ending or suspending access",
          body: [
            "We may suspend or close accounts that breach these terms or put other members at risk."
          ],
          items: [
            "You may cancel your account at any time by contacting us.",
            "We will provide reasonable notice before making substantive changes to these terms.",
            "Any obligations that should reasonably survive termination—such as confidentiality—remain in effect."
          ],
          note: "These terms are governed by the laws of the jurisdiction in which AACBT is established."
        }
      ],
      contact: {
        title: "Need clarity on these terms?",
        body: [
          "We are happy to explain how the agreement applies to your situation."
        ],
        email: "support@aacbt.de"
      }
    },
    accessibilityPage: {
      title: "Accessibility statement",
      updated: "Last reviewed: OCT 2025",
      intro: [
        "We want AACBT experiences to be usable for everyone, regardless of technology, ability, or context.",
        "This statement describes the steps we take today and the improvements we have scheduled."
      ],
      sections: [
        {
          title: "Our commitments",
          body: [
            "Accessibility and inclusive design are part of our planning, build, and review cycles."
          ],
          items: [
            "Follow WCAG 2.2 AA guidelines for new features and updates.",
            "Offer language choices and right-to-left layout support.",
            "Provide clear, jargon-free content and consistent navigation."
          ]
        },
        {
          title: "Measures we maintain",
          body: [
            "We combine automated tests with manual review by our team and partner organisations."
          ],
          items: [
            "Semantic HTML, descriptive links, and proper heading order across the site.",
            "Keyboard-only navigation and visible focus styles on interactive elements.",
            "Contrast checking and dark-mode testing to support different viewing needs."
          ]
        },
        {
          title: "Continuous improvement",
          body: [
            "Accessibility work is ongoing. We track feedback and update our backlog to address barriers quickly."
          ],
          items: [
            "Quarterly accessibility reviews with external advisors.",
            "Documented remediation plans for any issues we discover.",
            "Inclusive research sessions that invite participants with diverse needs."
          ],
          note: "If we fall short, we will prioritise fixes based on impact and communicate our timelines."
        }
      ],
      contact: {
        title: "Request assistance or share feedback",
        body: [
          "Let us know if you encounter an accessibility barrier so we can help.",
          "We aim to reply within two business days."
        ],
        email: "accessibility@aacbt.de"
      }
    },
    authPages: {
      loading: "Loading authentication data...",
      login: {
        subtitle: "Sign in to access your personalised resources and tools.",
        emailPlaceholder: "you@example.com",
        passwordLabel: "Password",
        pending: "Signing in...",
        action: "Continue",
        footerPrompt: "Need an account?"
      },
      signup: {
        title: "Create an account",
        subtitle: "Join as a member. Admin privileges can be assigned later by an existing admin.",
        emailPlaceholder: "you@example.com",
        confirmLabel: "Confirm password",
        pending: "Creating account...",
        action: "Create account",
        footerPrompt: "Already have an account?",
        success: "Account created! Redirecting to your account...",
        mismatch: "Passwords do not match."
      }
    },
    accountPage: {
      loading: "Loading account data...",
      subtitle: "Manage your session and view the current team roster.",
      roleLabel: "Role",
      coverAltSuffix: "cover",
      coverAltFallback: "Cover placeholder",
      team: {
        title: "Team roster",
        adminDescription: "Admins can see all members, including those awaiting approval.",
        memberDescription: "Members can see the approved roster.",
        realtime: "Updates appear in real time when members are added or edited.",
        empty: "No team members to display yet.",
        actions: {
          markPending: "Mark as pending",
          approve: "Approve",
          remove: "Remove",
          edit: "Edit"
        }
      },
      addMember: {
        title: "Add a team member",
        editTitle: "Edit team member",
        subtitle: "Create new roster entries that are stored in the shared Firestore roster.",
        editSubtitle: "Update details without removing and recreating the member.",
        fields: {
          id: "Member ID",
          prefix: "Prefix (optional)",
          role: "Role",
          verifiedBy: "Verified by (optional)",
          photoURL: "Photo URL (optional)",
          bio: "Short bio (optional)",
          approved: "Approved"
        },
        placeholders: {
          id: "member-123",
          name: "Alex Rivers",
          prefix: "Dr.",
          email: "alex@example.com",
          verifiedBy: "admin@yourapp.com",
          photoURL: "https://example.com/avatar.jpg",
          bio: "Trauma-informed therapist and program lead."
        },
        submit: {
          pending: "Saving...",
          action: "Add member",
          editPending: "Saving changes...",
          editAction: "Save changes",
          cancel: "Cancel edit"
        },
        error: {
          required: "Please provide an ID, name, and email address."
        },
        success: {
          created: "Member added to the roster.",
          updated: "Member details updated."
        },
        editing: "Editing {name}. Updates will modify the existing record."
      },
      copy: {
        title: "Site text overrides",
        subtitle: "Admins can update any public copy by editing the translation JSON below. Changes apply immediately.",
        languageLabel: "Language",
        editorLabel: "Overrides (JSON)",
        helper:
            "Start from the base translation, then adjust only the fields you need. Save an empty object to fall back to the defaults.",
        loadBase: "Load base translation",
        actions: {
          savePending: "Saving...",
          save: "Save overrides",
          resetPending: "Removing...",
          reset: "Remove overrides"
        },
        success: {
          saved: "Overrides saved. Refresh the public site to verify the changes.",
          reset: "Overrides removed. The site now uses the default copy."
        },
        errors: {
          invalidJson: "Enter valid JSON that resolves to an object."
        }
      },
      books: {
        title: "Manage books",
        subtitle: "Add, edit, or remove titles from the shared reading list.",
        fields: {
          title: "Title",
          author: "Author",
          description: "Description",
          coverURL: "Cover URL",
          linkURL: "Link URL",
          tags: "Tags (comma separated)"
        },
        placeholders: {
          title: "Deep work essentials",
          author: "Alex Rivers",
          description: "Short summary or why this title matters.",
          coverURL: "https://...",
          linkURL: "https://...",
          tags: "focus, habits"
        },
        listEmpty: "No books added yet.",
        actions: {
          addPending: "Saving...",
          add: "Add book",
          edit: "Edit",
          delete: "Delete",
          savePending: "Saving...",
          save: "Save changes",
          cancel: "Cancel"
        },
        errors: {
          titleRequired: "Title is required.",
          coverUrlInvalid: "Cover URL must be an http(s) link.",
          linkUrlInvalid: "Link URL must be an http(s) link."
        },
        success: {
          created: "Book added to the library.",
          updated: "Book updated.",
          removed: "Removed “{title}”.",
          fallbackTitle: "Book"
        }
      },
      resources: {
        title: "Manage resources",
        subtitle: "Keep guides, videos, and helpful links organised for members.",
        fields: {
          title: "Title",
          type: "Type",
          description: "Description",
          coverURL: "Cover URL",
          linkURL: "Link URL",
          tags: "Tags (comma separated)"
        },
        placeholders: {
          title: "Quick focus reset",
          description: "Short overview of the resource.",
          coverURL: "https://...",
          linkURL: "https://...",
          tags: "mindfulness, video"
        },
        types: {
          article: "Article",
          video: "Video",
          pdf: "PDF",
          link: "Link"
        },
        listEmpty: "No resources added yet.",
        actions: {
          addPending: "Saving...",
          add: "Add resource",
          edit: "Edit",
          delete: "Delete",
          savePending: "Saving...",
          save: "Save changes",
          cancel: "Cancel"
        },
        errors: {
          titleRequired: "Title is required.",
          coverUrlInvalid: "Cover URL must be an http(s) link.",
          linkUrlInvalid: "Link URL must be an http(s) link."
        },
        success: {
          created: "Resource added.",
          updated: "Resource updated.",
          removed: "Removed “{title}”.",
          fallbackTitle: "Resource"
        }
      }
    },
    adminInbox: {
      restricted: "Admins only.",
      title: "Contact inbox",
      empty: "No messages yet.",
      subject: "Subject",
      timestampPending: "pending timestamp"
    },
    language: { select: "Select language", names: { en: "English", ar: "العربية", he: "עברית" } },
    theme: { switchToLight: "Switch to light", switchToDark: "Switch to dark" },
    inlineEdit: {
      edit: "Edit",
      save: "Save",
      saving: "Saving...",
      cancel: "Cancel",
      saved: "Saved"
    }
  },

  ar: {
    brand: "AACBT",
    nav: { about: "نبذة", services: "الخدمات", team: "الفريق", resources: "الموارد", contact: "تواصل", inbox: "الوارد" },
    auth: { login: "تسجيل الدخول", signup: "إنشاء حساب", signout: "تسجيل الخروج", admin: "مسؤول", myAccount: "حسابي" },
    hero: {
      title: "برامج مبنية على الأدلة لذهن أكثر هدوءًا وصفاءً.",
      body: "أدوات عملية ومسارات تعلم منظمة — عبر الإنترنت أو حضوريًا. تعلّم مهاراتك وابنِ المرونة وتقدّم للأمام.",
      cta: "ابدأ الآن", explore: "استكشف الموارد",
      features: [
        { title: "التوتر والتركيز", text: "أدوات لتقليل القلق وتحسين التركيز." },
        { title: "المهارات أولاً", text: "أساليب آمنة ومتدرجة وتعاونية." },
        { title: "للأفراد والفرق", text: "برامج مناسبة للعمر والسياق." }
      ]
    },
    highlights: ["✔️ محتوى مبني على البحث","✔️ وحدات واضحة ومنظمة","✔️ الخصوصية أولاً","✔️ خيارات عبر الإنترنت وحضوريًا"],
    about: {
      eyebrow: "نبذة",
      title: "أساليب عملية مبسّطة",
      copy: "نركز على مهارات قابلة للتطبيق فورًا مع خطط واضحة وشفافة لتعرف دائمًا ما الخطوة التالية.",
      cards: [
        { title: "منهجية وأهداف واضحة", text: "معالم واضحة وتتبع للتقدم." },
        { title: "قصيرة أو معمّقة", text: "من جولات مهارية قصيرة إلى برامج أعمق." },
        { title: "شفافة وأخلاقية", text: "توقعات واضحة وخصوصية حسب التصميم." }
      ]
    },
    services: {
      eyebrow: "الخدمات", title: "ماذا نقدّم", copy: "مصممة للوضوح والمرونة وتأثير واقعي.", allTitle: "جميع الخدمات", allCopy: "تعرف بتفصيل على ما نقدمه.",
      items: [
        { title: "أدوات للتوتر والقلق", text: "ممارسات يومية للهدوء والوضوح." },
        { title: "العادات والتركيز", text: "روتينات مستندة إلى الأدلة للثبات." },
        { title: "برامج للفرق", text: "ورش خفيفة ومتابعات." },
        { title: "مهارات المرونة", text: "تعرض تدريجي لبناء الثقة." },
        { title: "مكتبة التعلم", text: "أدلة وقوائم فحص ودروس مصغرة." },
        { title: "جلسات عبر الإنترنت", text: "جدولة مرنة مع الخصوصية." }
      ]
    },
    team: { eyebrow: "الفريق", title: "تعرّف على مجموعتنا", copy: "فريق صغير مكرّس لأساليب واضحة قابلة للتنفيذ.", loading: "جاري تحميل قائمة الفريق...", signInToView: "سجّل الدخول لعرض قائمة الفريق.", none: "لا يوجد أعضاء بعد.", member: "عضو", approved: "مُعتمد", pending: "بانتظار الاعتماد", verifiedBy: "تم التحقق بواسطة" },
    resources: { eyebrow: "الموارد", title: "المكتبة", copy: "كتب وأدلة وفيديوهات مختارة لنتائج سريعة.", books: "الكتب", resources: "الموارد", view: "عرض", by: "بقلم", noneBooks: "ستظهر الكتب هنا مع نمو المكتبة.", noneRes: "ستظهر المقالات والفيديوهات هنا لاحقًا.", items: "عنصر", itemsPlural: "عناصر", noLink: "لا يوجد رابط" },
    contact: {
      eyebrow: "تواصل", title: "تواصل معنا", copy: "أخبرنا باحتياجاتك وسنعود إليك عبر البريد الإلكتروني.",
      sendAMessage: "أرسل رسالة", howWeWork: "كيف نعمل",
      bullets: ["نحن فريق صغير؛ عادة نرد خلال يوم عمل واحد.","لا حاجة للمكالمات — التنسيق عبر البريد الإلكتروني.","شارك فقط ما يناسبك مشاركته."],
      name: "الاسم الكامل", email: "البريد الإلكتروني", subject: "الموضوع", message: "اكتب رسالتك...", send: "إرسال",
      ok: "شكرًا! استلمنا رسالتك.", err: "تعذر إرسال الرسالة الآن.", sending: "جارٍ الإرسال..."
    },
    notFound: { title: "الصفحة غير موجودة", copy: "الصفحة التي تبحث عنها غير موجودة.", goHome: "العودة للرئيسية" },
    footer: { privacy: "الخصوصية", accessibility: "إمكانية الوصول", terms: "الشروط", rights: "جميع الحقوق محفوظة." },
    privacyPage: {
      title: "سياسة الخصوصية",
      updated: "آخر تحديث: اكتوبر 2025",
      intro: [
        "نحترم خصوصيتك ونجمع أقل قدر ممكن من البيانات الشخصية.",
        "توضح هذه السياسة نوع المعلومات التي نجمعها، وكيف نستخدمها، والخيارات المتاحة لك."
      ],
      sections: [
        {
          title: "المعلومات التي نجمعها",
          body: [
            "نجمع الحد الأدنى من البيانات اللازمة لتقديم خدماتنا ودعم الأعضاء."
          ],
          items: [
            "بيانات التواصل التي تشاركها عند إرسال رسالة.",
            "بيانات الحساب مثل الاسم والبريد الإلكتروني والدور عند التسجيل.",
            "مؤشرات استخدام مجمعة ومجهولة الهوية لمتابعة الأداء."
          ]
        },
        {
          title: "كيف نستخدم المعلومات",
          body: [
            "نعالج البيانات الشخصية لأغراض واضحة وموثقة تتعلق بالخدمات التي تطلبها."
          ],
          items: [
            "الرد على الاستفسارات وتقديم الخدمات التي تطلبها.",
            "الحفاظ على تسجيل دخول آمن وصلاحيات مناسبة للأعضاء والمشرفين.",
            "تحسين المواد التعليمية من خلال مراجعة الاتجاهات المجهولة."
          ]
        },
        {
          title: "كيف نخزن البيانات ونحميها",
          body: [
            "يتم تخزين المعلومات في أنظمة مشفرة ومحدودة الوصول. يقتصر الوصول على أعضاء الفريق المدربين الذين يحتاجون البيانات لأداء مهامهم."
          ],
          items: [
            "نراجع صلاحيات الوصول بانتظام ونعطّل الحسابات غير الضرورية.",
            "يتم تشفير النسخ الاحتياطية والاحتفاظ بها لفترة محدودة لدعم التعافي من الكوارث.",
            "لا نبيع المعلومات الشخصية ولا نشاركها مع المعلنين."
          ]
        },
        {
          title: "حقوقك وخياراتك",
          body: [
            "أنت من يحدد كيفية استخدام معلوماتك."
          ],
          items: [
            "طلب نسخة من البيانات الشخصية التي نحتفظ بها عنك.",
            "طلب تصحيح المعلومات غير الدقيقة أو حذف حسابك.",
            "سحب الموافقة على المراسلات الاختيارية في أي وقت."
          ],
          note: "نرد على طلبات الخصوصية الموثقة خلال يومي عمل."
        }
      ],
      contact: {
        title: "أسئلة حول الخصوصية؟",
        body: [
          "راسل مسؤول الخصوصية لدينا وسنعود إليك سريعًا."
        ],
        email: "privacy@aacbt.de"
      }
    },
    termsPage: {
      title: "شروط الخدمة",
      updated: "سارية من اكتوبر 2025",
      intro: [
        "باستخدامك لخدمات AACBT فإنك توافق على هذه الشروط.",
        "إذا كان لديك اتفاق مكتوب منفصل معنا، فسيكون له الأسبقية عند التعارض مع هذه الشروط."
      ],
      sections: [
        {
          title: "من يمكنه استخدام الخدمة",
          body: [
            "تم تصميم برامج AACBT للبالغين القادرين على إبرام اتفاق ملزم."
          ],
          items: [
            "يجب أن تكون في سن 18 عامًا على الأقل أو لديك موافقة ولي أمر.",
            "تلتزم بتقديم معلومات دقيقة عند إنشاء الحساب أو تحديثه.",
            "أنت مسؤول عن حماية بيانات الدخول الخاصة بك."
          ]
        },
        {
          title: "استخدام الخدمة بمسؤولية",
          body: [
            "نقدم محتوى تعليميًا وأدوات عملية. AACBT ليست بديلاً عن الرعاية الطبية أو النفسية المتخصصة."
          ],
          items: [
            "عدم استخدام الخدمات بطريقة تخالف القوانين أو اللوائح المعمول بها.",
            "تجنب مشاركة معلومات خاصة عن الآخرين دون موافقتهم.",
            "التواصل مع مختص مرخّص أو خدمات الطوارئ إذا كنت بحاجة إلى دعم عاجل."
          ]
        },
        {
          title: "المحتوى والملكية الفكرية",
          body: [
            "تظل المواد التعليمية والعلامة التجارية وكود المنصة ملكًا لـ AACBT أو الجهات المرخِّصة لنا."
          ],
          items: [
            "تحصل على ترخيص محدود وقابل للإلغاء لاستخدام الموارد لتطويرك الشخصي أو احتياجات فريقك.",
            "لا يجوز نسخ المواد أو إعادة بيعها أو توزيعها دون إذن خطي مسبق.",
            "قد نستخدم الملاحظات التي تشاركها لتحسين الخدمة دون الإشارة إلى اسمك."
          ]
        },
        {
          title: "إنهاء أو تعليق الوصول",
          body: [
            "قد نعلّق أو نغلق الحسابات التي تخالف هذه الشروط أو تعرّض الأعضاء الآخرين للخطر."
          ],
          items: [
            "يمكنك إلغاء حسابك في أي وقت عبر التواصل معنا.",
            "سنقدم إشعارًا مناسبًا قبل إجراء تغييرات جوهرية على هذه الشروط.",
            "تستمر الالتزامات التي يُفترض منطقيًا استمرارها — مثل السرية — بعد الإنهاء."
          ],
          note: "تخضع هذه الشروط لقوانين الولاية التي تعمل فيها AACBT."
        }
      ],
      contact: {
        title: "تحتاج إلى توضيح حول الشروط؟",
        body: [
          "يسعدنا شرح كيفية انطباق الاتفاق على وضعك."
        ],
        email: "support@aacbt.de"
      }
    },
    accessibilityPage: {
      title: "بيان إمكانية الوصول",
      updated: "آخر مراجعة: اكتوبر 2025",
      intro: [
        "نرغب في أن تكون تجارب AACBT متاحة للجميع بغض النظر عن التقنية أو القدرة أو السياق.",
        "يوضح هذا البيان الخطوات الحالية وخطط التحسين المستمرة."
      ],
      sections: [
        {
          title: "التزاماتنا",
          body: [
            "إمكانية الوصول والتصميم الشامل جزء من التخطيط والتنفيذ والمراجعة لدينا."
          ],
          items: [
            "اتباع إرشادات WCAG 2.2 مستوى AA عند تطوير المزايا الجديدة والتحديثات.",
            "تقديم خيارات اللغة ودعم الاتجاه من اليمين إلى اليسار.",
            "توفير محتوى واضح خالٍ من المصطلحات المعقدة وملاحة متسقة."
          ]
        },
        {
          title: "التدابير الحالية",
          body: [
            "نجمع بين الاختبارات الآلية والمراجعة اليدوية بواسطة فريقنا وشركائنا."
          ],
          items: [
            "استخدام HTML دلالي وروابط وصفية وتسلسل عناوين صحيح في الموقع.",
            "دعم التنقل عبر لوحة المفاتيح وإبراز واضح لحالة التركيز للعناصر التفاعلية.",
            "التحقق من التباين واختبار الوضع الداكن لتلبية احتياجات العرض المختلفة."
          ]
        },
        {
          title: "التحسين المستمر",
          body: [
            "يستمر عمل إمكانية الوصول باستمرار، ونسجل الملاحظات ونتابع تنفيذ الإصلاحات بسرعة."
          ],
          items: [
            "مراجعات فصلية لإمكانية الوصول بالتعاون مع مستشارين خارجيين.",
            "خطط معالجة موثقة لأي مشكلات نكتشفها.",
            "جلسات بحث شاملة تدعو مشاركين بقدرات واحتياجات متنوعة."
          ],
          note: "إذا قصّرنا، سنعطي الأولوية للإصلاحات حسب الأثر ونشارك جداولنا الزمنية."
        }
      ],
      contact: {
        title: "بحاجة إلى دعم أو لديك ملاحظات؟",
        body: [
          "أخبرنا إذا واجهت عائقًا في إمكانية الوصول لنتمكن من المساعدة.",
          "نهدف للرد خلال يومي عمل."
        ],
        email: "accessibility@aacbt.de"
      }
    },
    authPages: {
      loading: "جارٍ تحميل بيانات المصادقة...",
      login: {
        subtitle: "سجّل الدخول للوصول إلى مواردك وأدواتك المخصّصة.",
        emailPlaceholder: "you@example.com",
        passwordLabel: "كلمة المرور",
        pending: "جارٍ تسجيل الدخول...",
        action: "متابعة",
        footerPrompt: "تحتاج إلى حساب؟"
      },
      signup: {
        title: "إنشاء حساب",
        subtitle: "انضم كعضو. يمكن منح صلاحيات المشرف لاحقًا بواسطة مشرف حالي.",
        emailPlaceholder: "you@example.com",
        confirmLabel: "تأكيد كلمة المرور",
        pending: "جارٍ إنشاء الحساب...",
        action: "إنشاء حساب",
        footerPrompt: "لديك حساب بالفعل؟",
        success: "تم إنشاء الحساب! سيتم تحويلك إلى حسابك...",
        mismatch: "كلمتا المرور غير متطابقتين."
      }
    },
    accountPage: {
      loading: "جارٍ تحميل بيانات الحساب...",
      subtitle: "أدِر جلستك واطّلع على قائمة الفريق الحالية.",
      roleLabel: "الدور",
      coverAltSuffix: "غلاف",
      coverAltFallback: "صورة غلاف بديلة",
      team: {
        title: "قائمة الفريق",
        adminDescription: "يمكن للمشرفين رؤية جميع الأعضاء، بما في ذلك المنتظر اعتمادهم.",
        memberDescription: "يمكن للأعضاء رؤية القائمة المعتمدة.",
        realtime: "تظهر التحديثات مباشرة عند إضافة الأعضاء أو تعديلهم.",
        empty: "لا يوجد أعضاء لعرضهم بعد.",
        actions: {
          markPending: "تعيين كقيد الانتظار",
          approve: "اعتماد",
          remove: "إزالة",
          edit: "تحرير"
        }
      },
      addMember: {
        title: "إضافة عضو إلى الفريق",
        editTitle: "تحرير عضو الفريق",
        subtitle: "أنشئ سجلات جديدة يتم حفظها في قائمة الفريق على Firestore.",
        editSubtitle: "حدّث التفاصيل بدون إزالة العضو وإعادة إضافته.",
        fields: {
          id: "معرّف العضو",
          prefix: "اللقب (اختياري)",
          role: "الدور",
          verifiedBy: "تم التحقق بواسطة (اختياري)",
          photoURL: "رابط الصورة (اختياري)",
          bio: "نبذة قصيرة (اختياري)",
          approved: "معتمد"
        },
        placeholders: {
          id: "member-123",
          name: "ليلى أحمد",
          prefix: "د.",
          email: "layla@example.com",
          verifiedBy: "admin@yourapp.com",
          photoURL: "https://example.com/avatar.jpg",
          bio: "أخصائية علاج معرفي سلوكي وقائدة البرنامج."
        },
        submit: {
          pending: "جارٍ الحفظ...",
          action: "إضافة عضو",
          editPending: "جارٍ حفظ التعديلات...",
          editAction: "حفظ التغييرات",
          cancel: "إلغاء التعديل"
        },
        error: {
          required: "يرجى إدخال المعرّف والاسم والبريد الإلكتروني."
        },
        success: {
          created: "تمت إضافة العضو إلى القائمة.",
          updated: "تم تحديث بيانات العضو."
        },
        editing: "جاري تعديل {name}. أي تغييرات ستُحدث السجل الحالي."
      },
      copy: {
        title: "Site text overrides",
        subtitle: "Admins can update any public copy by editing the translation JSON below. Changes apply immediately.",
        languageLabel: "Language",
        editorLabel: "Overrides (JSON)",
        helper:
            "Start from the base translation, then adjust only the fields you need. Save an empty object to fall back to the defaults.",
        loadBase: "Load base translation",
        actions: {
          savePending: "Saving...",
          save: "Save overrides",
          resetPending: "Removing...",
          reset: "Remove overrides"
        },
        success: {
          saved: "Overrides saved. Refresh the public site to verify the changes.",
          reset: "Overrides removed. The site now uses the default copy."
        },
        errors: {
          invalidJson: "Enter valid JSON that resolves to an object."
        }
      },
      books: {
        title: "إدارة الكتب",
        subtitle: "أضف أو حرّر أو أزل العناوين من قائمة القراءة المشتركة.",
        fields: {
          title: "العنوان",
          author: "المؤلف",
          description: "الوصف",
          coverURL: "رابط الغلاف",
          linkURL: "رابط خارجي",
          tags: "وسوم (مفصولة بفواصل)"
        },
        placeholders: {
          title: "أساسيات العمل العميق",
          author: "ليلى أحمد",
          description: "ملخص قصير أو سبب أهمية هذا العنوان.",
          coverURL: "https://...",
          linkURL: "https://...",
          tags: "تركيز، عادات"
        },
        listEmpty: "لم تتم إضافة كتب بعد.",
        actions: {
          addPending: "جارٍ الحفظ...",
          add: "إضافة كتاب",
          edit: "تحرير",
          delete: "حذف",
          savePending: "جارٍ الحفظ...",
          save: "حفظ التغييرات",
          cancel: "إلغاء"
        },
        errors: {
          titleRequired: "العنوان مطلوب.",
          coverUrlInvalid: "يجب أن يكون رابط الغلاف عبر http(s).",
          linkUrlInvalid: "يجب أن يكون الرابط عبر http(s)."
        },
        success: {
          created: "تمت إضافة الكتاب إلى المكتبة.",
          updated: "تم تحديث الكتاب.",
          removed: "تمت إزالة “{title}”.",
          fallbackTitle: "كتاب"
        }
      },
      resources: {
        title: "إدارة الموارد",
        subtitle: "نظّم الأدلة والفيديوهات والروابط المفيدة للأعضاء.",
        fields: {
          title: "العنوان",
          type: "النوع",
          description: "الوصف",
          coverURL: "رابط الغلاف",
          linkURL: "رابط خارجي",
          tags: "وسوم (مفصولة بفواصل)"
        },
        placeholders: {
          title: "إعادة ضبط التركيز بسرعة",
          description: "نظرة سريعة على المورد.",
          coverURL: "https://...",
          linkURL: "https://...",
          tags: "يقظة، فيديو"
        },
        types: {
          article: "مقال",
          video: "فيديو",
          pdf: "ملف PDF",
          link: "رابط"
        },
        listEmpty: "لم تتم إضافة موارد بعد.",
        actions: {
          addPending: "جارٍ الحفظ...",
          add: "إضافة مورد",
          edit: "تحرير",
          delete: "حذف",
          savePending: "جارٍ الحفظ...",
          save: "حفظ التغييرات",
          cancel: "إلغاء"
        },
        errors: {
          titleRequired: "العنوان مطلوب.",
          coverUrlInvalid: "يجب أن يكون رابط الغلاف عبر http(s).",
          linkUrlInvalid: "يجب أن يكون الرابط عبر http(s)."
        },
        success: {
          created: "تمت إضافة المورد.",
          updated: "تم تحديث المورد.",
          removed: "تمت إزالة “{title}”.",
          fallbackTitle: "مورد"
        }
      }
    },
    adminInbox: {
      restricted: "للمشرفين فقط.",
      title: "صندوق الوارد للتواصل",
      empty: "لا توجد رسائل بعد.",
      subject: "الموضوع",
      timestampPending: "طابع زمني قيد الانتظار"
    },
    language: { select: "اختر اللغة", names: { en: "English", ar: "العربية", he: "עברית" } },
    theme: { switchToLight: "التبديل إلى الوضع الفاتح", switchToDark: "التبديل إلى الوضع الداكن" },
    inlineEdit: {
      edit: "تعديل",
      save: "حفظ",
      saving: "جاري الحفظ...",
      cancel: "إلغاء",
      saved: "تم الحفظ"
    }
  },

  he: {
    brand: "AACBT",
    nav: { about: "אודות", services: "שירותים", team: "צוות", resources: "משאבים", contact: "יצירת קשר", inbox: "דואר נכנס" },
    auth: { login: "התחברות", signup: "הרשמה", signout: "התנתקות", admin: "מנהל", myAccount: "החשבון שלי" },
    hero: {
      title: "תכניות מבוססות ראיות לשקט ובהירות.",
      body: "כלים מעשיים ומסלולי למידה מובנים — אונליין או פרונטלי. לומדים מיומנויות, בונים חוסן ומתקדמים.",
      cta: "התחילו", explore: "עיינו במשאבים",
      features: [
        { title: "לחץ ומיקוד", text: "כלים להפחתת דאגה ולשיפור המיקוד." },
        { title: "מיומנויות תחילה", text: "גישות בטוחות, מדורגות ושיתופיות." },
        { title: "לצוותים וליחידים", text: "תכניות מותאמות גיל והקשר." }
      ]
    },
    highlights: ["✔️ תוכן מבוסס מחקר","✔️ מודולים ברורים ומובנים","✔️ פרטיות תחילה","✔️ אונליין ובאופן פרונטלי"],
    about: {
      eyebrow: "אודות",
      title: "שיטות מעשיות, בפשטות",
      copy: "מתמקדים במיומנויות שניתן ליישם מיד, עם תכניות שקופות כך שתמיד יודעים מה הצעד הבא.",
      cards: [
        { title: "מובנה וממוקד מטרה", text: "אבני דרך ברורות ומעקב התקדמות." },
        { title: "קצר או מעמיק", text: "מסשנים קצרים ועד תכניות מעמיקות." },
        { title: "שקוף ואתי", text: "ציפיות ברורות ופרטיות כברירת מחדל." }
      ]
    },
    services: {
      eyebrow: "שירותים", title: "מה אנחנו מציעים", copy: "מעוצב לבהירות, גמישות והשפעה ממשית.", allTitle: "כל השירותים", allCopy: "צלילה עמוקה למה שאנו מציעים.",
      items: [
        { title: "כלים ללחץ וחרדה", text: "תרגולים יומיומיים לשקט ובהירות." },
        { title: "הרגלים ומיקוד", text: "שגרות מבוססות ראיות להתמדה." },
        { title: "תכניות לצוותים", text: "סדנאות קלילות ומעקב." },
        { title: "מיומנויות חוסן", text: "חשיפה מדורגת לבניית ביטחון." },
        { title: "ספריית למידה", text: "מדריכים, צ׳ק-ליסטים וקורסים קצרים." },
        { title: "מפגשים אונליין", text: "תזמון גמיש ופרטיות." }
      ]
    },
    team: { eyebrow: "צוות", title: "הכירו את הקבוצה", copy: "צוות קטן ומסור המתמקד בשיטות ישימות וברורות.", loading: "טוען את רשימת הצוות...", signInToView: "התחברו כדי לראות את רשימת הצוות.", none: "אין חברי צוות כרגע.", member: "חבר צוות", approved: "מאושר", pending: "ממתין לאישור", verifiedBy: "אומת על ידי" },
    resources: { eyebrow: "משאבים", title: "ספרייה", copy: "ספרים, מדריכים וסרטונים לנצחונות מהירים.", books: "ספרים", resources: "משאבים", view: "צפה", by: "מאת", noneBooks: "ספרים יופיעו כאן ככל שהספרייה תגדל.", noneRes: "מאמרים וסרטונים יופיעו כאן בהמשך.", items: "פריט", itemsPlural: "פריטים", noLink: "אין קישור" },
    contact: {
      eyebrow: "יצירת קשר", title: "דברו איתנו", copy: "ספרו לנו קצת על הצרכים שלכם — נחזור במייל.",
      sendAMessage: "שליחת הודעה", howWeWork: "איך אנחנו עובדים",
      bullets: ["אנחנו צוות קטן; לרוב חוזרים תוך יום עסקים.","אין צורך בשיחות — מתאמים במייל.","שתפו רק מה שנוח לכם."],
      name: "שם מלא", email: "אימייל", subject: "נושא", message: "הודעתכם...", send: "שליחה",
      ok: "תודה! ההודעה התקבלה.", err: "לא ניתן לשלוח כעת.", sending: "שולח..."
    },
    notFound: { title: "העמוד לא נמצא", copy: "העמוד שחיפשתם אינו קיים.", goHome: "חזרה לדף הבית" },
    footer: { privacy: "פרטיות", accessibility: "נגישות", terms: "תנאים", rights: "כל הזכויות שמורות." },
    privacyPage: {
      title: "מדיניות פרטיות",
      updated: "עודכן לאחרונה: אוקטובר 2025",
      intro: [
        "אנחנו מכבדים את פרטיותכם ואוספים רק את המידע שנדרש.",
        "מסמך זה מפרט איזה מידע אנו אוספים, כיצד אנו משתמשים בו ואילו זכויות עומדות לרשותכם."
      ],
      sections: [
        {
          title: "מידע שאנו אוספים",
          body: [
            "אנחנו אוספים את המינימום הנדרש כדי לספק את השירות ולתמוך בחברים."
          ],
          items: [
            "פרטי קשר שאתם מוסרים כאשר אתם שולחים לנו הודעה.",
            "פרטי חשבון כגון שם, כתובת אימייל ותפקיד בעת ההרשמה.",
            "מדדי שימוש מצטברים ואנונימיים לצורך ניטור ביצועים."
          ]
        },
        {
          title: "כיצד אנו משתמשים במידע",
          body: [
            "אנו מעבדים נתונים אישיים למטרות מוגדרות וברורות הקשורות לשירות שביקשתם."
          ],
          items: [
            "מענה לפניות ומתן השירותים שביקשתם.",
            "שמירה על אימות והרשאות מאובטחים עבור חברים ומנהלים.",
            "שיפור חומרי הלמידה באמצעות ניתוח מגמות אנונימיות."
          ]
        },
        {
          title: "אופן השמירה וההגנה על נתונים",
          body: [
            "המידע נשמר במערכות מוצפנות ומוגבלות גישה. רק חברי צוות מיומנים שצריכים את הנתונים לתפקידם מקבלים גישה."
          ],
          items: [
            "אנחנו בוחנים הרשאות גישה באופן קבוע ומבטלים חשבונות שאינם נדרשים.",
            "גיבויים מוצפנים ונשמרים לפרק זמן מוגבל לצורכי התאוששות מאסון.",
            "אנחנו לא מוכרים מידע אישי ולא משתפים אותו עם מפרסמים."
          ]
        },
        {
          title: "הזכויות והבחירות שלכם",
          body: [
            "אתם מחליטים כיצד נעשה שימוש במידע שלכם."
          ],
          items: [
            "בקשו עותק של הנתונים האישיים שאנו מחזיקים לגביכם.",
            "בקשה לתקן מידע שאינו מדויק או למחוק את החשבון שלכם.",
            "משיכת הסכמה לתקשורת רשות בכל עת."
          ],
          note: "אנחנו משיבים לבקשות פרטיות מאומתות בתוך שני ימי עסקים."
        }
      ],
      contact: {
        title: "שאלות בנושא פרטיות?",
        body: [
          "כתבו למוביל הפרטיות שלנו ונשיב במהירות."
        ],
        email: "privacy@aacbt.de"
      }
    },
    termsPage: {
      title: "תנאי שימוש",
      updated: "בתוקף אוקטובר 2025",
      intro: [
        "באמצעות שימוש בשירותי AACBT אתם מסכימים לתנאים אלו.",
        "אם קיים הסכם כתוב אחר בינינו לביניכם, ההסכם ההוא גובר במקרה של סתירה."
      ],
      sections: [
        {
          title: "מי יכול להשתמש בשירות",
          body: [
            "התכניות של AACBT מיועדות לבגירים המסוגלים ליצור הסכם מחייב."
          ],
          items: [
            "עליכם להיות בני 18 ומעלה או להחזיק באישור הורה או אפוטרופוס.",
            "אתם מתחייבים לספק מידע נכון בעת יצירת חשבון או עדכונו.",
            "אחריות שמירת פרטי ההתחברות מוטלת עליכם."
          ]
        },
        {
          title: "שימוש אחראי בשירות",
          body: [
            "אנו מספקים תוכן לימודי וכלים מעשיים. AACBT אינה תחליף לטיפול רפואי או נפשי מותאם אישית."
          ],
          items: [
            "אל תשתמשו בשירות בניגוד לחוקים או לתקנות החלים.",
            "הימנעו משיתוף מידע אישי של אחרים ללא הסכמתם.",
            "פנו לאיש מקצוע מוסמך או לשירותי חירום אם אתם זקוקים לעזרה מיידית."
          ]
        },
        {
          title: "תוכן וקניין רוחני",
          body: [
            "החומרים, המיתוג וקוד הפלטפורמה הם בבעלות AACBT או בעלי הרישיון שלנו."
          ],
          items: [
            "אתם מקבלים רישיון מוגבל ובטל לשימוש במשאבים לצרכים האישיים או לצורכי הצוות שלכם.",
            "אין להעתיק, למכור מחדש או להפיץ את החומרים ללא אישור בכתב.",
            "משוב שתחליטו לשתף עשוי לשמש לשיפור השירות ללא ציון שמכם."
          ]
        },
        {
          title: "סיום או השעיית גישה",
          body: [
            "אנחנו רשאים להשעות או לסגור חשבונות שמפרים תנאים אלה או מסכנים משתמשים אחרים."
          ],
          items: [
            "תוכלו לבטל את החשבון בכל עת באמצעות פנייה אלינו.",
            "נספק הודעה סבירה לפני שינוי מהותי של תנאים אלו.",
            "חובות שצריכות להמשיך להתקיים — כמו שמירה על סודיות — יישארו בתוקף."
          ],
          note: "תנאים אלו כפופים לחוקי המדינה שבה פועלת AACBT."
        }
      ],
      contact: {
        title: "צריכים הבהרה לגבי התנאים?",
        body: [
          "נשמח להסביר כיצד ההסכם חל עליכם."
        ],
        email: "support@aacbt.de"
      }
    },
    accessibilityPage: {
      title: "הצהרת נגישות",
      updated: "עודכנה לאחרונה: אוקטובר 2025",
      intro: [
        "אנחנו רוצים שהחוויה ב-AACBT תהיה נגישה לכל אחד, ללא קשר לטכנולוגיה, ליכולת או להקשר.",
        "ההצהרה מפרטת את הצעדים שאנו נוקטים כיום ואת התכניות לשיפור."
      ],
      sections: [
        {
          title: "המחויבויות שלנו",
          body: [
            "נגישות ועיצוב כוללני משולבים בשלבי התכנון, הפיתוח והבקרה שלנו."
          ],
          items: [
            "עמידה בהנחיות WCAG 2.2 ברמת AA עבור פיצ'רים חדשים ועדכונים.",
            "מתן אפשרויות שפה ותמיכה בפריסת ימין-לשמאל.",
            "סיפוק תוכן ברור וללא ז׳רגון וממשק ניווט עקבי."
          ]
        },
        {
          title: "אמצעים שאנו מקיימים",
          body: [
            "אנחנו משלבים בדיקות אוטומטיות עם בדיקות ידניות של הצוות שלנו ושותפים מקצועיים."
          ],
          items: [
            "HTML סמנטי, קישורים מתארים וסדר כותרות נכון בכל האתר.",
            "ניווט מלא באמצעות מקלדת וסגנונות פוקוס גלויים לרכיבים אינטראקטיביים.",
            "בדיקות ניגודיות ובחינת מצב כהה לתמיכה בצורכי צפייה שונים."
          ]
        },
        {
          title: "שיפור מתמשך",
          body: [
            "עבודת הנגישות נמשכת כל הזמן. אנו מתעדים משוב ומקדמים תיקון חסמים במהירות."
          ],
          items: [
            "סקירות נגישות רבעוניות עם יועצים חיצוניים.",
            "תכניות טיפול מתועדות לכל בעיה שנאתר.",
            "מחקר משתמשים כוללני הכולל משתתפים עם צרכים מגוונים."
          ],
          note: "אם נתקלתם בקושי, נטפל בו לפי מידת ההשפעה ונעדכן בלוחות הזמנים."
        }
      ],
      contact: {
        title: "זקוקים לעזרה או רוצים לשתף משוב?",
        body: [
          "ספרו לנו אם גיליתם חסם נגישות כדי שנוכל לסייע.",
          "מטרתנו להשיב בתוך שני ימי עסקים."
        ],
        email: "accessibility@aacbt.de"
      }
    },
    authPages: {
      loading: "טוען נתוני הזדהות...",
      login: {
        subtitle: "התחברו כדי לגשת למשאבים ולכלים המותאמים אישית.",
        emailPlaceholder: "you@example.com",
        passwordLabel: "סיסמה",
        pending: "מתחבר...",
        action: "המשך",
        footerPrompt: "צריך חשבון?"
      },
      signup: {
        title: "יצירת חשבון",
        subtitle: "הצטרפו כחברים. הרשאות מנהל ניתנות בהמשך על ידי מנהל קיים.",
        emailPlaceholder: "you@example.com",
        confirmLabel: "אימות סיסמה",
        pending: "יוצר חשבון...",
        action: "יצירת חשבון",
        footerPrompt: "כבר יש לכם חשבון?",
        success: "החשבון נוצר! מעבירים אתכם לחשבון...",
        mismatch: "הסיסמאות אינן תואמות."
      }
    },
    accountPage: {
      loading: "טוען נתוני חשבון...",
      subtitle: "נהלו את ההתחברות וצפו ברשימת הצוות הנוכחית.",
      roleLabel: "תפקיד",
      coverAltSuffix: "עטיפה",
      coverAltFallback: "תמונת עטיפה חלופית",
      team: {
        title: "רשימת הצוות",
        adminDescription: "מנהלים יכולים לראות את כל החברים, כולל ממתינים לאישור.",
        memberDescription: "חברים רואים את הרשימה המאושרת.",
        realtime: "עדכונים מופיעים בזמן אמת כאשר מוסיפים או עורכים חברים.",
        empty: "אין חברי צוות להצגה כרגע.",
        actions: {
          markPending: "סימון כממתין",
          approve: "אישור",
          remove: "הסרה",
          edit: "עריכה"
        }
      },
      addMember: {
        title: "הוספת חבר צוות",
        editTitle: "עריכת חבר צוות",
        subtitle: "צרו רשומות חדשות שנשמרות ברשימת ה-Firestore המשותפת.",
        editSubtitle: "עדכנו פרטים בלי להסיר ולהוסיף מחדש.",
        fields: {
          id: "מזהה חבר",
          prefix: "תואר (אופציונלי)",
          role: "תפקיד",
          verifiedBy: "אומת על ידי (אופציונלי)",
          photoURL: "קישור לתמונה (אופציונלי)",
          bio: "תיאור קצר (אופציונלי)",
          approved: "מאושר"
        },
        placeholders: {
          id: "member-123",
          name: "נועה כהן",
          prefix: "ד\"ר",
          email: "noa@example.com",
          verifiedBy: "admin@yourapp.com",
          photoURL: "https://example.com/avatar.jpg",
          bio: "מטפלת קוגניטיבית-התנהגותית מובילה."
        },
        submit: {
          pending: "שומר...",
          action: "הוספת חבר",
          editPending: "שומר שינויים...",
          editAction: "שמירת שינויים",
          cancel: "ביטול עריכה"
        },
        error: {
          required: "נא להזין מזהה, שם וכתובת דוא\"ל."
        },
        success: {
          created: "החבר נוסף לרשימה.",
          updated: "פרטי החבר עודכנו."
        },
        editing: "עורכים את {name}. השינויים יחולו על הרשומה הקיימת."
      },
      copy: {
        title: "Site text overrides",
        subtitle: "Admins can update any public copy by editing the translation JSON below. Changes apply immediately.",
        languageLabel: "Language",
        editorLabel: "Overrides (JSON)",
        helper:
            "Start from the base translation, then adjust only the fields you need. Save an empty object to fall back to the defaults.",
        loadBase: "Load base translation",
        actions: {
          savePending: "Saving...",
          save: "Save overrides",
          resetPending: "Removing...",
          reset: "Remove overrides"
        },
        success: {
          saved: "Overrides saved. Refresh the public site to verify the changes.",
          reset: "Overrides removed. The site now uses the default copy."
        },
        errors: {
          invalidJson: "Enter valid JSON that resolves to an object."
        }
      },
      books: {
        title: "ניהול ספרים",
        subtitle: "הוסיפו, ערכו או הסירו כותרים מספריית הקריאה המשותפת.",
        fields: {
          title: "כותרת",
          author: "מחבר",
          description: "תיאור",
          coverURL: "קישור לעטיפה",
          linkURL: "קישור",
          tags: "תגיות (מופרדות בפסיקים)"
        },
        placeholders: {
          title: "יסודות עבודה עמוקה",
          author: "נועה כהן",
          description: "תקציר קצר או למה הכותר חשוב.",
          coverURL: "https://...",
          linkURL: "https://...",
          tags: "מיקוד, הרגלים"
        },
        listEmpty: "לא נוספו ספרים עדיין.",
        actions: {
          addPending: "שומר...",
          add: "הוספת ספר",
          edit: "עריכה",
          delete: "מחיקה",
          savePending: "שומר...",
          save: "שמירת שינויים",
          cancel: "ביטול"
        },
        errors: {
          titleRequired: "חובה להזין כותרת.",
          coverUrlInvalid: "קישור העטיפה חייב להיות ב-http(s).",
          linkUrlInvalid: "הקישור חייב להיות ב-http(s)."
        },
        success: {
          created: "הספר נוסף לספרייה.",
          updated: "הספר עודכן.",
          removed: "הוסר “{title}”.",
          fallbackTitle: "ספר"
        }
      },
      resources: {
        title: "ניהול משאבים",
        subtitle: "ארגנו מדריכים, וידאו וקישורים שימושיים לחברים.",
        fields: {
          title: "כותרת",
          type: "סוג",
          description: "תיאור",
          coverURL: "קישור לעטיפה",
          linkURL: "קישור",
          tags: "תגיות (מופרדות בפסיקים)"
        },
        placeholders: {
          title: "איפוס מיקוד מהיר",
          description: "תיאור קצר של המשאב.",
          coverURL: "https://...",
          linkURL: "https://...",
          tags: "קשב, וידאו"
        },
        types: {
          article: "מאמר",
          video: "וידאו",
          pdf: "PDF",
          link: "קישור"
        },
        listEmpty: "לא נוספו משאבים עדיין.",
        actions: {
          addPending: "שומר...",
          add: "הוספת משאב",
          edit: "עריכה",
          delete: "מחיקה",
          savePending: "שומר...",
          save: "שמירת שינויים",
          cancel: "ביטול"
        },
        errors: {
          titleRequired: "חובה להזין כותרת.",
          coverUrlInvalid: "קישור העטיפה חייב להיות ב-http(s).",
          linkUrlInvalid: "הקישור חייב להיות ב-http(s)."
        },
        success: {
          created: "המשאב נוסף.",
          updated: "המשאב עודכן.",
          removed: "הוסר “{title}”.",
          fallbackTitle: "משאב"
        }
      }
    },
    adminInbox: {
      restricted: "למנהלים בלבד.",
      title: "דואר נכנס לפניות",
      empty: "אין הודעות עדיין.",
      subject: "נושא",
      timestampPending: "חותמת זמן ממתינה"
    },
    language: { select: "בחרו שפה", names: { en: "English", ar: "العربية", he: "עברית" } },
    theme: { switchToLight: "החלפה למצב בהיר", switchToDark: "החלפה למצב כהה" },
    inlineEdit: {
      edit: "עריכה",
      save: "שמירה",
      saving: "שומר...",
      cancel: "ביטול",
      saved: "נשמר"
    }
  }
};

let translationOverrides = {};

export function setTranslationOverrides(overrides) {
  if (overrides && typeof overrides === "object") {
    translationOverrides = overrides;
  } else {
    translationOverrides = {};
  }
}

function getNestedValue(source, parts) {
  if (!source || typeof source !== "object") return undefined;
  return parts.reduce((acc, part) => {
    if (acc && typeof acc === "object" && Object.prototype.hasOwnProperty.call(acc, part)) {
      return acc[part];
    }
    return undefined;
  }, source);
}

export function t(key, lang = "en") {
  const parts = key.split(".");

  const fromOverrides =
      getNestedValue(translationOverrides[lang], parts) ??
      (lang !== "en" ? getNestedValue(translationOverrides.en, parts) : undefined);
  if (fromOverrides !== undefined) return fromOverrides;

  const fromTranslations = getNestedValue(translations[lang], parts);
  if (fromTranslations !== undefined) return fromTranslations;

  if (lang !== "en") {
    const fallbackOverride = getNestedValue(translationOverrides.en, parts);
    if (fallbackOverride !== undefined) return fallbackOverride;
  }

  const fallbackTranslation = getNestedValue(translations.en, parts);
  return fallbackTranslation !== undefined ? fallbackTranslation : key;
}
